/**
 * MODULE_INFO — centrale uitleg per module.
 *
 * Wordt gebruikt door:
 *  - <PageHeader> voor titel + korte uitleg + "wat kan je hier zien?"
 *  - Sidebar tooltips (hover-uitleg)
 *
 * Elke module: {label, kort, zie[]}
 *  - label: naam van de module (wordt getoond als H1 op de pagina)
 *  - kort: 1 zin — wat is deze module en waarvoor is hij
 *  - zie: bullets — wat kun je hier zien / doen
 */

export const MODULE_INFO = {
  dashboard: {
    label: "Dashboard",
    kort: "Startpagina met een persoonlijk overzicht van alles wat vandaag jouw aandacht vraagt.",
    zie: [
      "Aankomende vergaderingen en trainingen",
      "Openstaande tickets en verlofaanvragen",
      "Assets die keuring naderen of verlopen zijn",
      "Verjaardagen van collega's deze maand",
      "Jouw taken van vandaag en overtijd-taken",
    ],
  },
  taken: {
    label: "Takenoverzicht",
    kort: "CRM stuurt de werkdag aan — manager/directeur delegeert taken (incidenteel of terugkerend); medewerkers zien wat er van hen verwacht wordt en rapporteren terug.",
    zie: [
      "Categorieën-dashboard met kleur per taakcategorie",
      "Mijn taken vandaag, overtijd en aankomend",
      "Alle taken met filters op categorie, status, medewerker en zoekterm",
      "Manager-rapportage: dag/week/maand uitdraai naar Excel — hoeveel taken zijn er gegeven en uitgevoerd",
      "Bidirectionele koppeling met agenda: afvinken hier = agenda bijgewerkt",
    ],
  },
  reminders: {
    label: "Reminders",
    kort: "Terugkerende herinneringen om bedrijfsprocessen op tijd uit te voeren (bv. maandelijkse controle blusmiddelen, jaarlijkse APK, wekelijkse voorraadcheck).",
    zie: [
      "Alle actieve herinneringen met hun volgende trigger-datum",
      "Welke medewerker(s) verantwoordelijk zijn per reminder",
      "'Voltooid'-actie zet automatisch de volgende trigger-datum (op basis van interval)",
      "Overtijd-herinneringen in rood, met counter in de sidebar",
      "Geschiedenis: hoe vaak elke reminder al uitgevoerd is",
    ],
  },
  berichten: {
    label: "Berichten & Broadcast",
    kort: "Interne chat per rol-kanaal — snel afstemmen zonder mail. Managers kunnen broadcasten naar hele rolgroepen (bijv. alle buitendienst).",
    zie: [
      "Kanalen per rol: algemeen, management, buitendienst, KCC, Salesteam, etc.",
      "Live berichten met ongelezen-badges per kanaal",
      "Broadcast-optie voor managers: gaat naar alle leden van het kanaal",
      "Verwijderen (verplaatst naar Archief) door aanmaker of manager",
    ],
  },
  dashboards: {
    label: "Uitgebreide dashboards",
    kort: "KPI's, trends en top-N per module (Werkbonnen, Klanten, Facturen, Offertes). Voor management-inzicht en presentaties.",
    zie: [
      "Overview met 8 KPI's over de hele operatie",
      "Werkbonnen: aantal, omzet, statusverdeling, trends, top-10 monteurs en klanten",
      "Klanten: OutSmart + BB + Vaste klanten samengevoegd, top steden en omzetklanten",
      "Facturen & Offertes: aantallen, waardes, conversiepercentage",
      "Download als PDF-presentatie of Excel-rapport",
    ],
  },
  rechten: {
    label: "Medewerkers & rechten",
    kort: "Eén overzicht van wie wat mag in BPTplus: per rol de standaard, per medewerker de uitzonderingen. Drie niveaus: lezen, uitvoeren, beheren. Wijzigen kan alleen de COO.",
    zie: [
      "Per rol: matrix met alle onderdelen — klik op een cel en kies geen / lezen / uitvoeren / beheren",
      "Per medewerker: wat de rol geeft, wat deze persoon afwijkend heeft (amber) en 'terug naar rolstandaard'",
      "Rol van een medewerker wijzigen — synchroon met de medewerkerkaart en de hiërarchie",
      "Elke wijziging wordt direct opgeslagen, gelogd in de audit log en geldt meteen in menu, pagina's en API",
      "Managers kijken mee; alleen de COO kan wijzigen",
    ],
  },
  "rol-permissies": {
    label: "Rol-standaardrechten",
    kort: "Stel per rol in welke modules zichtbaar zijn (lezen) en waar wijzigingen gedaan mogen worden (bewerken). Voor gepersonaliseerde toegang zonder maatwerk per gebruiker.",
    zie: [
      "Per rol: aparte lijsten voor 'Lezen' (menu zichtbaar) en 'Bewerken' (mag wijzigen)",
      "Wijzigingen gelden meteen voor alle bestaande én nieuwe gebruikers van die rol",
      "'Terug naar default' om aanpassingen ongedaan te maken",
      "Bewerken kan niet zonder lezen — bewerking-checkbox wordt automatisch geblokkeerd",
      "Custom-permissies per gebruiker (in Account-beheer) hebben altijd voorrang",
    ],
  },
  crediteuren: {
    label: "Crediteuren-workflow",
    kort: "Complete factuur-workflow: medewerker uploadt → COO (operationeel directeur) accordeert → automatische batch-betaling via Bunq elke vrijdag 11:00.",
    zie: [
      "Uploaden factuur met PDF én meerdere extra bijlagen (foto's, opdrachtbevestiging)",
      "Uitgebreid filteren op zoek, bedrag, status, upload-periode",
      "Multi-select: meerdere facturen tegelijk accorderen of afwijzen",
      "Audit-trail per factuur: wie/wanneer/waarom",
      "Volgende geplande batch en klaarstaande facturen met totaalbedrag",
    ],
  },
  verlof: {
    label: "Verlof",
    kort: "Vakantiedagen aanvragen (iedereen) en verwerken (manager+directeur). Aanvragen automatisch gekoppeld aan werkdagen-kalender per medewerker.",
    zie: [
      "Mijn aanvragen: recht, opgenomen, aangevraagd en resterende dagen",
      "Nieuwe aanvraag als hele dag(en) óf op uur-basis",
      "'Te verwerken'-tab voor manager: alle open aanvragen met 1-klik goedkeuren/afwijzen",
      "Medewerker krijgt automatisch een mail bij goedkeuring/afwijzing",
    ],
  },
  leads: {
    label: "In de buurt — leads",
    kort: "Openbare leads gescraped uit 34 grote NL-steden. Voor buitendienst om te bellen tijdens rustige momenten.",
    zie: [
      "Filters op stad, status en zoekterm",
      "Kleurcodering per status: afspraak gepland = groen, klant geworden = donkergroen, geen interesse = rood",
      "Klik op een lead: telefoongesprek loggen, status bijwerken, notities toevoegen",
      "Manager kan hier zien welke leads door welke medewerker opgevolgd zijn",
    ],
  },
  "vaste-klanten": {
    label: "Vaste klanten (leadopvolging)",
    kort: "CRM voor bestaande klanten die keuring nodig hebben. Route-planner om efficiënt langs meerdere klanten te rijden.",
    zie: [
      "Kanban-bord met statussen: nieuw → gebeld → afspraak → bezocht",
      "Route-planner met Haversine-afstandsberekening voor optimale volgorde",
      "Excel-import van klantlijsten",
      "Mail-templates voor herinneringen keuring",
    ],
  },
  "uitval-planner": {
    label: "Uitval-planner (ik heb veel uitval)",
    kort: "Voor keurmeesters: als je onverwacht een gat in je route hebt, vind snel vaste klanten in de buurt van je huidige positie en plan direct in — er wordt automatisch een werkbon op jouw naam gemaakt.",
    zie: [
      "Startpunt = jouw laatste OutSmart-werkbon van vandaag (of handmatige postcode)",
      "Straal-zoek (radius km) rondom dat startpunt",
      "Alleen vaste klanten met keurdatum binnen X dagen die nog niet ingepland zijn",
      "1-klik inplanning: werkbon aangemaakt + vaste klant naar status 'ingepland' + workflow-log",
      "OutSmart write-push wanneer OutSmart-employee-nummer via COO is gekoppeld",
    ],
  },
  klachten: {
    label: "Klachten",
    kort: "Klachten van klanten centraal registreren en afhandelen. Automatisch een taak aangemaakt voor klantenservice bij nieuwe klacht.",
    zie: [
      "Klachtnummers oplopend genummerd voor traceerbaarheid",
      "Onderwerp, type, beschrijving en huidige afhandelaar",
      "Automatische taak-aanmaak: bij nieuwe klacht → taak voor de klantenservice-rol",
      "Statuswijziging bij verplaatsen naar volgende afhandelaar",
    ],
  },
  tickets: {
    label: "Tickets",
    kort: "Interne tickets tussen collega's (bv. IT-vraag, HR-verzoek, storing melden). Duidelijk wie het oppakt en waar het staat.",
    zie: [
      "Ticket-nummer, onderwerp, aanmaker en huidige status",
      "Toewijzen aan collega, opmerkingen loggen, sluiten",
      "Filters op status/prioriteit/afdeling",
    ],
  },
  vergaderingen: {
    label: "Vergaderingen",
    kort: "Interne meetings plannen incl. deelnemers, herinneringen en agendapunten.",
    zie: [
      "Aankomende meetings met datum, tijd en locatie",
      "Herinneringen 24u en 1u van tevoren",
      "Deelnemers per meeting en agenda-onderwerpen",
    ],
  },
  assets: {
    label: "Assets",
    kort: "Voertuigen, gereedschap en apparatuur met keuringsdata. Voorkomt dat je met verlopen keuring op pad gaat.",
    zie: [
      "Alle bedrijfsassets met status en volgende-keurings-datum",
      "Kleur: groen = actueel, oranje = binnen 30 dagen, rood = verlopen",
      "Documenten en historie per asset (kentekens, keuringsrapporten, etc.)",
    ],
  },
  werkbonnen: {
    label: "Werkbonnen",
    kort: "Uitvoering-bonnen voor klantopdrachten. Buitendienst vult tijden, materialen en kilometers in.",
    zie: [
      "Alle bonnen met status: ingepland / uitgevoerd / gefactureerd",
      "Per bon: klant, datum, aantal uren, gebruikte materialen",
      "Sync met OutSmart CRM voor facturatie",
    ],
  },
  projecten: {
    label: "Projecten BB",
    kort: "Brandwerende bewerkingen — grote projecten met meerdere applicateurs en documenten.",
    zie: [
      "Projectstatussen: opgestart → in uitvoering → opgeleverd → gefactureerd",
      "Documentatie per project (opdracht, offerte, oplevering)",
      "Applicateurs die aan project gekoppeld zijn",
    ],
  },
  "account-beheer": {
    label: "Accounts & rechten",
    kort: "Gebruikers-accounts beheren: e-mail, rol, gekoppelde medewerker en per-account custom rechten die de rol-defaults overrulen.",
    zie: [
      "Alle actieve gebruikers met hun rol",
      "Custom permissies per gebruiker (bovenop rol-standaard)",
      "Reset wachtwoord, deactiveren, rol wijzigen",
    ],
  },
  archief: {
    label: "Archief (verwijderd)",
    kort: "Alle 'zachte verwijderingen' van de laatste 12 maanden — herstelbaar tot 1 jaar na verwijderdatum.",
    zie: [
      "Verwijderde items uit 12+ modules: klanten, projecten, werkbonnen, etc.",
      "Wie/wanneer verwijderd en originele collectie",
      "1-klik terugzetten of definitief verwijderen",
    ],
  },
  "audit-log": {
    label: "Audit-log",
    kort: "Volledig logboek van alle wijzigingen — wie deed wat, wanneer en aan welke record. Voor compliance en foutdetectie.",
    zie: [
      "Alle CREATE/UPDATE/DELETE/APPROVE-acties chronologisch",
      "Filter op module, gebruiker, actie of periode",
      "Details per actie: welke velden gewijzigd zijn",
    ],
  },
  "ai-instructies": {
    label: "AI-instructies",
    kort: "Per module een AI-systeemprompt en context-documenten. De AI-assistent gebruikt deze om binnen bedrijfsregels te blijven.",
    zie: [
      "15 voor-ingestelde templates (BMI, ES-TRIN, debiteuren, etc.)",
      "Instructietekst + uploads (PDF/DOCX/afbeeldingen als context)",
      "Per module kun je zien welke prompts actief zijn",
    ],
  },
  training: {
    label: "Training",
    kort: "Trainingsmateriaal, tutorial-video's en documentatie per rol. Iedereen kan lezen; alleen directie kan materiaal bewerken.",
    zie: [
      "Video's, documenten en checklists per onderwerp",
      "Voortgang per medewerker: welke trainingen zijn afgerond",
      "Zoeken en filteren op onderwerp of doelgroep",
    ],
  },
  mail: {
    label: "Mail-outbox",
    kort: "Alle automatisch verzonden e-mails van het systeem (Zoho SMTP). Overzicht van verzonden/gefaalde/concept-mails.",
    zie: [
      "Status per mail: concept / verzonden / gefaald met reden",
      "Retry-knop voor gefaalde mails",
      "SMTP-config zichtbaar (host/poort/gebruiker; wachtwoord verborgen)",
      "Test-mail knop om te controleren of Zoho-koppeling werkt",
    ],
  },
  onderhoudscontracten: {
    label: "Onderhoudscontracten",
    kort: "AI-gestuurde generator voor onderhoudsovereenkomsten (Word/PDF) volgens de vaste 18-secties-structuur met NL/BE prijslijst en staffelkortingen per objectsoort.",
    zie: [
      "Zoeken en filteren op klant, kenmerk, land, aanmaakperiode",
      "Nieuw contract via intake-formulier: klantgegevens, diensten met aantallen, gebouwen, bijzondere afspraken",
      "Automatisch staffelprijs uitrekenen per objectsoort (bijv. 8 blustoestellen = staffel 6–10 = € 26,00 p/st)",
      "Volledige aanbod aankruisbaar (☒/☐) — inclusief 'op aanvraag' categorieën (BMI, elektro, bouwkundig, droge blusleiding, BPT Diensten)",
      "Download in Word (.docx) én PDF, met BPT-briefpapier op elke pagina",
      "Tabellen worden niet gesplitst over pagina's; handtekeningblok komt op één pagina",
    ],
  },
  medewerkers: {
    label: "Medewerkers",
    kort: "Personeelsadministratie — alle medewerkers, hun rol, functie, contract en contactgegevens.",
    zie: [
      "Actieve medewerkers met naam, rol, functie, e-mail en telefoon",
      "Detailpagina met verlof-tegoed, gekoppeld user-account en bekwaamheden",
      "Alleen manager/directie kan bewerken; medewerker ziet 'Mijn gegevens'",
    ],
  },
  klachten: {
    label: "Klachten",
    kort: "Klachten van klanten registreren en opvolgen. Automatisch taak-aanmaak voor klantenservice bij een nieuwe klacht.",
    zie: [
      "Klacht-nummer, onderwerp, type, huidige status en afhandelaar",
      "Log van wijzigingen en communicatie",
      "Auto-taak in de rol 'klantenservice' zodra klacht binnenkomt",
    ],
  },
  vergaderingen: {
    label: "Vergaderingen",
    kort: "Meetings plannen met deelnemers, agenda en herinneringen (24u + 1u van tevoren).",
    zie: [
      "Aankomende meetings met datum, tijd en locatie",
      "Deelnemers per meeting en agenda-onderwerpen",
      "AI-samenvatting van vergaderingnotulen (indien opgegeven)",
    ],
  },
  assets: {
    label: "Assets",
    kort: "Voertuigen, gereedschap en apparatuur met keuringsdata — voorkomt dat je met verlopen keuring op pad gaat.",
    zie: [
      "Alle bedrijfsassets met status en volgende keuringsdatum",
      "Kleur-indicatie: groen = actueel, oranje = binnen 30 dagen, rood = verlopen",
      "Toegewezen medewerker per asset + historie",
    ],
  },
  bekwaamheden: {
    label: "Bekwaamheden",
    kort: "Certificeringen en vaardigheden per medewerker (REOB, VCA, NEN 3140) inclusief geldigheidsdata.",
    zie: [
      "Wie welke certificaten heeft en wanneer die verlopen",
      "Reminders bij naderende verval-datum",
      "Vereiste bekwaamheden per rol",
    ],
  },
  werkbonnen: {
    label: "Werkbonnen",
    kort: "Uitvoerings-bonnen voor klantopdrachten — buitendienst vult tijden, materialen en kilometers in.",
    zie: [
      "Alle bonnen met status ingepland / uitgevoerd / gefactureerd",
      "Per bon: klant, datum, aantal uren, gebruikte materialen",
      "Sync met OutSmart CRM voor facturatie",
    ],
  },
  projecten: {
    label: "Projecten BB",
    kort: "Brandwerende bewerkingen — grote projecten met meerdere applicateurs, documenten en fases.",
    zie: [
      "Statussen: opgestart → in uitvoering → opgeleverd → gefactureerd",
      "Documenten per project (opdracht, offerte, oplevering)",
      "Toegewezen applicateurs en projectleider",
    ],
  },
  "bb-klanten": {
    label: "BB Klantenlijst",
    kort: "Klanten die brandwerende bewerkingen (BB-projecten) hebben lopen of hebben gehad.",
    zie: [
      "Alle BB-klanten met contactgegevens en projecthistorie",
      "Filtering op status en periode",
      "Doorlink naar bijbehorende Projecten BB",
    ],
  },
  facturatie: {
    label: "Offertes & Facturen",
    kort: "Facturen en offertes vanuit OutSmart, met status, bedragen en download.",
    zie: [
      "Alle facturen en offertes met status (verstuurd/betaald/openstaand)",
      "Filter op klant, periode en status",
      "PDF-download per factuur/offerte",
    ],
  },
  offertes: {
    label: "Offertes",
    kort: "Alle offertes uit OutSmart met status-workflow (concept → verzonden → geaccepteerd/afgewezen → gefactureerd).",
    zie: [
      "Uitgebreide filters: klant, status, bedrag-range, datum-range",
      "Excel + CSV export van gefilterde selectie",
      "PDF-download per offerte + 1-klik omzetten naar factuur",
    ],
  },
  facturen: {
    label: "Facturen",
    kort: "Alle facturen uit OutSmart met betaalstatus, herinneringen en aanmaningen.",
    zie: [
      "Uitgebreide filters: klant, status (open/betaald/vervallen), bedrag, datum",
      "Excel + CSV export voor debiteurenoverzicht",
      "PDF-download + Bunq-koppeling voor automatische afboeking",
    ],
  },
  "outsmart-sync": {
    label: "OutSmart sync ↔",
    kort: "Bidirectionele koppeling BPTplus ↔ OutSmart. Read (OutSmart → BPTplus) en Write (BPTplus → OutSmart) worden hier beheerd. Push-queue laat zien welke wijzigingen nog naar OutSmart moeten en welke handmatig zijn omdat OutSmart-endpoint bevestigd moet worden.",
    zie: [
      "Read: 5 sync-knoppen (Relaties/Werkbonnen/Objecten/Materialen/Employees)",
      "Write-queue: pending/verstuurd/retry/handmatig met filter-tegels",
      "Retry en handmatig-actie per push",
      "Simulatie-modus (dry-run) voor testen",
      "Auto-detectie van OutSmart-endpoint uit meerdere varianten",
    ],
  },
  planbord: {
    label: "Planbord",
    kort: "Weekplanning van werkbonnen en medewerkers — zie in één oogopslag wie waar is.",
    zie: [
      "Grid per week met rijen per medewerker en kolommen per dag",
      "Slepen om werkbon naar andere medewerker/datum te verplaatsen",
      "Real-time sync met OutSmart",
    ],
  },
  crm: {
    label: "CRM — Relaties",
    kort: "Alle klantrelaties uit OutSmart: bedrijven, contactpersonen, historie en contract-status.",
    zie: [
      "Zoeken op naam, plaats, KvK-nummer",
      "Detailpagina met bijbehorende werkbonnen/facturen/offertes",
      "Directe koppeling naar OutSmart-record",
    ],
  },
  documenten: {
    label: "Documenten",
    kort: "Algemene bedrijfsdocumenten — huisregels, handboeken, procedures. Alleen managers uploaden.",
    zie: [
      "Alle documenten met categorie en versienummer",
      "Filter op categorie en zoekwoord",
      "Download of open in browser",
    ],
  },
  snelkoppelingen: {
    label: "Snelkoppelingen",
    kort: "Handige links naar veelgebruikte externe systemen (OutSmart, Bunq, portaal, etc.).",
    zie: [
      "Alle snelkoppelingen met korte omschrijving",
      "Openen in nieuw tabblad",
    ],
  },
  bestellingen: {
    label: "Bestellingen",
    kort: "Materiaal-bestellingen door medewerkers — automatisch gekoppeld aan leverancier bij verwerking door manager.",
    zie: [
      "Alle bestellingen met status open/verwerkt/geleverd",
      "Bestelformulier met vaste artikelen én overige artikelen",
      "Managers zien de te verwerken-lijst en kunnen leverancier toewijzen",
    ],
  },
  voorraad: {
    label: "Voorraad-teller",
    kort: "Real-time voorraad-tellingen door buitendienst per locatie of bus.",
    zie: [
      "Voorraad per locatie/bus met minimum-drempel",
      "Snel invoeren via telling-modus",
      "Overzicht van artikelen die bij-besteld moeten worden",
    ],
  },
  financieel: {
    label: "Financieel",
    kort: "Omzet, kosten, marges — voor managers een compleet financieel dashboard.",
    zie: [
      "Omzet per maand/kwartaal/jaar",
      "Kosten-overzicht per categorie",
      "Openstaande facturen en debiteurenstand",
    ],
  },
  commissieberekening: {
    label: "Commissieberekening",
    kort: "Automatische commissie-berekening voor verkopers op basis van gefactureerde omzet.",
    zie: [
      "Commissie-percentage per medewerker",
      "Excel-upload voor bulk-berekening",
      "PDF-uitdraai commissie-overzicht per periode",
    ],
  },
  training: {
    label: "Training",
    kort: "Trainingsmateriaal en video's per rol. Iedereen kan lezen; alleen directie kan bewerken.",
    zie: [
      "Video's, documenten en checklists per onderwerp",
      "Voortgang per medewerker: welke trainingen zijn afgerond",
      "Zoeken en filteren op onderwerp of doelgroep",
    ],
  },
  ontruimingsplan: {
    label: "Ontruimingsplan",
    kort: "Interactieve NEN 1414 / 6069 canvas-editor voor evacuatie-plattegronden.",
    zie: [
      "Canvas per gebouw/verdieping met sleepbare symbolen",
      "Export als PDF met legenda",
      "AI-check: is het plan volledig NEN-conform?",
    ],
  },
  locaties: {
    label: "Vestigingen",
    kort: "Bedrijfslocaties (kantoor, magazijn, filialen) met adres, contactgegevens en toegewezen medewerkers.",
    zie: [
      "Alle vestigingen met adres en telefoon",
      "Toegewezen medewerkers per locatie",
      "Kaart-view met alle vestigingen",
    ],
  },
  kalender: {
    label: "Agenda sync",
    kort: "Bidirectionele sync met externe agenda's (Google Calendar). Werkbonnen, taken en vergaderingen verschijnen automatisch.",
    zie: [
      "Overzicht van alle geplande events",
      "Sync-status per gebruiker",
      "Handmatig items pushen naar externe agenda",
    ],
  },
  "account-beheer": {
    label: "Accounts & rechten",
    kort: "Gebruikers-accounts beheren: e-mail, rol, gekoppelde medewerker, custom rechten.",
    zie: [
      "Alle actieve gebruikers met hun rol",
      "Custom permissies per gebruiker (bovenop rol-standaard)",
      "Reset wachtwoord, deactiveren, rol wijzigen",
    ],
  },
  "audit-log": {
    label: "Audit-log",
    kort: "Volledig logboek van alle wijzigingen — wie deed wat, wanneer en aan welke record.",
    zie: [
      "Alle CREATE/UPDATE/DELETE-acties chronologisch",
      "Filter op module, gebruiker, actie of periode",
      "Details per actie: welke velden gewijzigd zijn",
    ],
  },
  bunq: {
    label: "Bunq-betalingen",
    kort: "Bunq API-integratie voor SEPA batch-betalingen — automatisch elke vrijdag 11:00.",
    zie: [
      "Klaarstaande batch met totaalbedrag",
      "Betalings-log: succes/gefaald per opdracht",
      "Handmatige batch-uitvoering ('Verwerk NU')",
    ],
  },
  "inkomende-betalingen": {
    label: "Inkomende betalingen",
    kort: "Autonome AI-agent die Bunq-mutaties leest, matcht op openstaande facturen, boekt in OutSmart en KCC-mail verstuurt.",
    zie: [
      "Live sessie-status per Bunq-rekening (NL22/NL38 · NL20 BE)",
      "Poll & match — laatste 5 cijfers factuurnr, debiteurnr, klantnaam, bedrag",
      "Interne overboekingen (Ünal Beheer, Intermiko, BPT) automatisch uitgesloten",
      "Handmatig boeken tegen factuur-suggestie of vrij factuur-id",
      "KCC-samenvatting met kleurcodes (Geboekt NL/BE · Niet geboekt) via Zoho",
      "Volledig audit-trail per boeking",
    ],
  },
  "Inkomende betalingen": {
    label: "Inkomende betalingen",
    kort: "Autonome AI-agent die Bunq-mutaties leest, matcht op openstaande facturen, boekt in OutSmart en KCC-mail verstuurt.",
    zie: [
      "Live sessie-status per Bunq-rekening (NL22/NL38 · NL20 BE)",
      "Poll & match — laatste 5 cijfers factuurnr, debiteurnr, klantnaam, bedrag",
      "Interne overboekingen automatisch uitgesloten",
      "Handmatig boeken tegen factuur-suggestie of vrij factuur-id",
      "KCC-samenvatting met kleurcodes via Zoho",
    ],
  },
  "contante-betalingen": {
    label: "Contante betalingen",
    kort: "Contant ontvangen bedragen registreren en gecontroleerd afvoeren: keurmeester levert af → HR telt → directie verifieert en accordeert → betaling in OutSmart.",
    zie: [
      "Registratie: klant, werkbonnummer, factuurnummer, bedrag, keurmeester",
      "Stap 2: afgeleverd bij verantwoordelijke (datum + bedrag klopt ja/nee)",
      "Afvoer-ronde: HR telt en bevestigt, directie accordeert (vier-ogen)",
      "Na accordering automatisch als 'Contant' op de OutSmart-factuur geboekt",
      "Logboek per betaling, meldingen en Excel-export",
    ],
  },
  "Contante betalingen": {
    label: "Contante betalingen",
    kort: "Contant ontvangen bedragen registreren en gecontroleerd afvoeren (keurmeester → HR → directie → OutSmart).",
    zie: [
      "Registratie met klant, werkbon, factuur, bedrag en keurmeester",
      "Aflevering bevestigen met datum en bedragcontrole",
      "Afvoer-rondes met HR-telling en directie-accordering",
    ],
  },
  hierarchie: {
    label: "Hiërarchie (Groepen)",
    kort: "Rol-hiërarchie en aansturingsstructuur van het bedrijf — wie stuurt wie aan.",
    zie: [
      "Organigram-view met rol-relaties",
      "Verantwoordelijkheden per rol",
      "Toegewezen managers per medewerker",
    ],
  },
  verbeteringen: {
    label: "Verbetermeldingen",
    kort: "Ideeën en verbetervoorstellen van medewerkers — samen bouwen aan een betere organisatie.",
    zie: [
      "Alle meldingen met status (ontvangen/in behandeling/afgerond)",
      "Feedback door management",
      "Anonieme meldingen mogelijk",
    ],
  },
  "mijn-gegevens": {
    label: "Mijn gegevens",
    kort: "Jouw persoonlijke gegevens: adres, telefoon, geboortedatum, functie, gekoppelde locatie.",
    zie: [
      "Contactgegevens wijzigen",
      "Verlof-tegoed inzien",
      "Certificaten / bekwaamheden",
      "Wachtwoord wijzigen",
    ],
  },
  "ai-assistent": {
    label: "AI Assistent",
    kort: "Chat met Claude 4.5 voor snelle vragen, PDF-scannen, contract-check, e-mail opstellen — met BPT-context.",
    zie: [
      "Vrije chat met bedrijfs-context (klanten, facturen, medewerkers)",
      "PDF/afbeelding uploaden voor OCR + samenvatting",
      "Vraag stellen over module-instructies",
    ],
  },
};

/** Fallback voor onbekende module-keys. */
export const DEFAULT_MODULE_INFO = {
  label: "Module",
  kort: "Deze module heeft nog geen uitleg.",
  zie: [],
};

/** Haal info op via module-key. */
export function getModuleInfo(key) {
  return MODULE_INFO[key] || DEFAULT_MODULE_INFO;
}

/** Voorstel 55 — extra trefwoorden/synoniemen per pagina (pad → woorden) voor het menu-zoekveld. */
export const MENU_ZOEKWOORDEN = {
  "/urenregels": ["uren", "pauze", "overuren", "reistijd", "contracturen", "regelset", "staffel", "contractafspraken", "arbeidsovereenkomst"],
  "/mijn-uren": ["uren", "pauze", "overuren", "urenbriefje", "tijd schrijven", "werktijd"],
  "/uren-keuren": ["uren", "pauze", "overuren", "goedkeuren", "keuren", "nacorrectie", "loon", "nmbrs"],
  "/roosterkalender": ["feestdagen", "rooster", "kalender", "vrije dagen", "personeelsgroep", "sla", "werkdagen"],
  "/kalender": ["kalender", "agenda", "afspraken", "planning"],
  "/mail-dashboard": ["mail", "e-mail", "import", "herverwerking", "archief", "mailbox", "dump", "export", "zoho mail", "wachtrij"],
  "/postvak": ["mail", "e-mail", "inbox", "postvak", "verzonden", "sent", "archief", "zoeken in mail"],
  "/klachten": ["klacht", "klachten", "reob", "melding", "escalatie", "ontevreden"],
  "/rechten": ["rechten", "rollen", "permissies", "toegang", "rechtendashboard", "wie mag wat"],
  "/medewerkers": ["medewerker", "personeel", "hr", "contract", "medewerkerkaart", "arbeidsovereenkomst"],
  "/sync-health": ["fase b", "nulmeting", "mailvolledigheid", "back-up", "backup", "export", "data-export", "workdrive", "koppeling", "sync", "bijlagen"],
  "/ontwikkelen": ["ontwikkelen", "voorstellen", "codevoorstellen", "goedkeuringen", "modules", "audit", "claude"],
  "/beveiliging": ["beveiliging", "2fa", "inlog", "sessies", "fraude"],
  "/debiteurenbeheer": ["debiteuren", "openstaand", "aanmaning", "incasso", "betaling"],
  "/werkbonnen": ["werkbon", "outsmart", "keuring", "monteur"],
  "/planbord": ["planning", "planbord", "rooster", "monteurs", "beschikbaarheid"],
  "/verlof": ["verlof", "vakantie", "vrij", "ziek"],
  "/declaraties": ["declaratie", "bonnetje", "onkosten"],
  "/mijn-reiskosten": ["reiskosten", "kilometers", "km"],
  "/documenten": ["documenten", "bestanden", "bijlagen", "kennisbank"],
  "/vaste-klanten": ["vaste klanten", "klantkaart", "leads", "outsmart", "prospects"],
  "/klantdossiers": ["klantdossier", "dossier", "tijdlijn", "klant"],
};

/** Voorstel 55 — sub-onderdelen/tabbladen met een eigen URL; `ouder` = pagina waar het recht van afhangt. */
export const MENU_SUBONDERDELEN = [
  { ouder: "/mail-dashboard", to: "/mail-dashboard?tab=mailboxes", label: "Mailboxen", woorden: ["mailbox", "import", "dump", "accounts"] },
  { ouder: "/mail-dashboard", to: "/mail-dashboard?tab=koppelcontrole", label: "Koppelcontrole & herverwerking", woorden: ["herverwerking", "wachtrij", "archief", "workdrive", "export", "koppelcontrole", "herstel"] },
  { ouder: "/mail-dashboard", to: "/mail-dashboard?tab=outbox", label: "Verzonden mail", woorden: ["verzonden", "outbox", "sent"] },
  { ouder: "/mail-dashboard", to: "/mail-dashboard?tab=ochtendbriefing", label: "Ochtendbriefing", woorden: ["briefing", "ochtend"] },
  { ouder: "/urenregels", to: "/urenregels?tab=migratie", label: "Migratie 0 verschillen", woorden: ["migratie", "migratietest", "verschillen"] },
  { ouder: "/urenregels", to: "/urenregels?tab=proef", label: "Proefberekening", woorden: ["proef", "proefberekening", "rekenen"] },
  { ouder: "/urenregels", to: "/urenregels?tab=koppelingen", label: "Koppelingen medewerkers", woorden: ["koppeling", "persoon", "regelset"] },
  { ouder: "/urenregels", to: "/urenregels?tab=contractafspraken", label: "Contractafspraken (controle)", woorden: ["contract", "contractafspraken", "arbeidsovereenkomst", "controle"] },
  { ouder: "/ontwikkelen", to: "/ontwikkelen?tab=goedkeuringen", label: "Goedkeuringen", woorden: ["goedkeuring", "akkoord"] },
  { ouder: "/ontwikkelen", to: "/ontwikkelen?tab=code", label: "Codevoorstellen", woorden: ["voorstel", "code", "codevoorstellen"] },
  { ouder: "/ontwikkelen", to: "/ontwikkelen?tab=koppelingen", label: "Koppelingen", woorden: ["koppeling", "api", "integratie"] },
  { ouder: "/ontwikkelen", to: "/ontwikkelen?tab=audit", label: "Audit", woorden: ["audit", "log"] },
  { ouder: "/sync-health", to: "/sync-health#backup", label: "Data-export & back-up", woorden: ["back-up", "backup", "export", "data-export", "workdrive", "sleutel"] },
  { ouder: "/sync-health", to: "/sync-health#faseb", label: "Fase B — schone klantdata uit OutSmart", woorden: ["fase b", "import", "outsmart", "niet gekoppeld", "bewijsrapport"] },
  { ouder: "/mijn-uren", to: "/mijn-uren?tab=weekoverzicht", label: "Weekoverzicht contract", woorden: ["week", "weekoverzicht", "overuren", "contract"] },
];
